'use client';

import { useCallback, useRef, useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';

import { useFormatCheck } from '../hooks/useFormatCheck';
import type { FormatCheckerVariantConfig } from '../types';

const LOADER_STEPS = [
  { icon: 'fa-cloud-upload', message: 'Uploading your resume securely...' },
  { icon: 'fa-cog', message: 'Creating your private workspace...' },
  { icon: 'fa-refresh', message: 'Importing your resume...' },
  { icon: 'fa-check-circle', message: 'Import successful! Redirecting...' },
] as const;

type FormatCheckerWidgetProps = {
  config: FormatCheckerVariantConfig;
};

export function FormatCheckerWidget({ config }: FormatCheckerWidgetProps) {
  const {
    state,
    errorMessage,
    selectedFile,
    resumeText,
    loaderStep,
    tidbit,
    handleFileSelect,
    setResumeText,
    submit,
    reset,
  } = useFormatCheck({
    variant: config.id,
    targetFormat: config.targetFormat,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [inlineErrorVisible, setInlineErrorVisible] = useState(false);

  const onFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;
      if (file) {
        setInlineErrorVisible(false);
      }
      handleFileSelect(file);
      if (event.target) {
        event.target.value = '';
      }
    },
    [handleFileSelect],
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);
      const file = event.dataTransfer.files?.[0] || null;
      if (file) {
        setInlineErrorVisible(false);
      }
      handleFileSelect(file);
    },
    [handleFileSelect],
  );

  const progressValue =
    state === 'idle' ? 0 : ((loaderStep + 1) / LOADER_STEPS.length) * 100;

  if (state === 'loading') {
    const step =
      LOADER_STEPS[loaderStep] ??
      LOADER_STEPS[0] ?? {
        icon: 'fa-cloud-upload',
        message: 'Uploading your resume securely...',
      };
    return (
      <div className="fc-widget-card fc-widget-card--loading">
        <div className="fc-loader">
          <div className="fc-loader__icon-container">
            <i className={`fa ${step.icon}`} />
          </div>
          <div className="fc-loader__step">
            Step {loaderStep + 1} of {LOADER_STEPS.length}
          </div>
          <Progress value={progressValue} className="fc-loader__progress" />
          <div className="fc-loader__message">{step.message}</div>
          {tidbit ? (
            <div className="fc-loader__tidbits">
              <span>{tidbit}</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="fc-widget-card fc-widget-card--error">
        <div className="fc-error">
          <i className="fa fa-exclamation-triangle fc-error__icon" />
          <h3>Import Failed</h3>
          <p>{errorMessage || 'We could not import your resume. Please try again.'}</p>
          <Button onClick={reset} className="fc-btn-main" type="button">
            <i className="fa fa-refresh" /> Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fc-widget-card">
      <div
        className="fc-upload-area"
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={onDrop}
      >
        <label
          className={`fc-upload-box ${isDragOver ? 'fc-upload-box--dragover' : ''} ${selectedFile ? 'fc-upload-box--selected' : ''}`}
        >
          <i className="fa-solid fa-cloud-arrow-up fc-upload-box__icon" />
          <h3>{config.uploadLabel}</h3>
          <p className="fc-upload-box__filename">
            {selectedFile ? selectedFile.name : 'Drag & Drop or Click to Upload'}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="fc-upload-box__input"
            onChange={onFileChange}
          />
        </label>
      </div>

      <div className="fc-or-section">
        <hr />
        <span>OR</span>
        <hr />
      </div>

      <div className="fc-paste-area">
        <Textarea
          placeholder="Paste your resume contents here..."
          value={resumeText}
          onChange={(event) => {
            setResumeText(event.target.value);
            if (event.target.value.trim()) {
              setInlineErrorVisible(false);
            }
          }}
          className="fc-paste-area__textarea"
          required
        />
        {inlineErrorVisible ? (
          <Alert className="fc-inline-error" variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Please upload a resume file or paste your resume contents.
            </AlertDescription>
          </Alert>
        ) : null}
        <Button
          onClick={() => {
            if (!selectedFile && !resumeText.trim()) {
              setInlineErrorVisible(true);
              return;
            }
            setInlineErrorVisible(false);
            submit();
          }}
          className="fc-btn-main"
          type="button"
          aria-label={config.ctaButtonLabel}
        >
          <i className="fa-solid fa-magnifying-glass-chart" />{' '}
          {config.ctaButtonLabel}
        </Button>
      </div>
    </div>
  );
}
